import { Component, type ReactNode } from 'react';
import classnames from 'classnames';
import styles from './Dropdown-style.scss';
import dibsCss from 'dibs-css';

const ALIGN_LEFT = 'left';
const ALIGN_RIGHT = 'right';
const ALIGN_CENTER = 'center';

const SIZE_SMALL = 'small';
const SIZE_MEDIUM = 'medium';
const SIZE_MEDIUM_LARGE = 'mediumLarge';
const SIZE_LARGE = 'large';
const SIZE_EXTRA_LARGE = 'extraLarge';

const alignConstants = {
    ALIGN_LEFT,
    ALIGN_RIGHT,
    ALIGN_CENTER,
} as const;
const sizeConstants = {
    SIZE_SMALL,
    SIZE_MEDIUM,
    SIZE_MEDIUM_LARGE,
    SIZE_LARGE,
    SIZE_EXTRA_LARGE,
} as const;

export { alignConstants, sizeConstants };

type valueof<T> = T[keyof T];

type Props = {
    children: ReactNode[];
    align?: valueof<typeof alignConstants>;
    size?: valueof<typeof sizeConstants>;
    hasDropdownHeader?: boolean;
    forceOpen?: boolean;
    className?: string;
    dropdownClassName?: string;
    bodyClassName?: string;
    onHide?: () => void;
    onShow?: () => void;
    showDelayDuration?: number;
    hideDelayDuration?: number;
};

type State = {
    isClient: boolean;
    isVisible: boolean;
};

class Dropdown extends Component<Props, State> {
    static defaultProps = {
        align: ALIGN_LEFT,
        size: SIZE_MEDIUM,
        hasDropdownHeader: false,
        forceOpen: false,
    };

    constructor(props: Props) {
        super(props);

        this.state = {
            isClient: false,
            isVisible: false,
        };

        this.dropdown = null;
        this.showTimeout = null;
        this.hideTimeout = null;
        this.hide = this.hide.bind(this);
        this.show = this.show.bind(this);
        this.handleMouseEnter = this.handleMouseEnter.bind(this);
        this.handleMouseLeave = this.handleMouseLeave.bind(this);
        this.adjustLeftStyle = this.adjustLeftStyle.bind(this);
    }

    componentDidMount(): void {
        window.addEventListener('orientationchange', this.adjustLeftStyle);
        // Initialize alignments
        this.setState({ isClient: true }, () => {
            this.adjustLeftStyle();
        });
    }

    componentDidUpdate(prevProps: Props): void {
        if (!prevProps.forceOpen && this.props.forceOpen) {
            this.clearTimeouts();
            this.show();
        } else if (prevProps.forceOpen && !this.props.forceOpen) {
            this.clearTimeouts();
            this.hide();
        }
    }

    componentWillUnmount(): void {
        window.removeEventListener('orientationchange', this.adjustLeftStyle);
        this.clearTimeouts();
    }

    dropdown: HTMLElement | null;
    showTimeout: number | null;
    hideTimeout: number | null;

    clearTimeouts(): void {
        if (this.showTimeout) {
            clearTimeout(this.showTimeout);
            this.showTimeout = null;
        }
        if (this.hideTimeout) {
            clearTimeout(this.hideTimeout);
            this.hideTimeout = null;
        }
    }

    handleMouseEnter(): void {
        if (this.hideTimeout) {
            clearTimeout(this.hideTimeout);
            this.hideTimeout = null;
        }

        if (this.state.isVisible || this.showTimeout) {
            return;
        }

        this.showTimeout = window.setTimeout(() => {
            this.showTimeout = null;
            this.show();
        }, this.props.showDelayDuration ?? 0);
    }

    handleMouseLeave(): void {
        if (this.showTimeout) {
            clearTimeout(this.showTimeout);
            this.showTimeout = null;
            return;
        }

        if (!this.state.isVisible || this.hideTimeout) {
            return;
        }

        this.hideTimeout = window.setTimeout(() => {
            this.hideTimeout = null;
            this.hide();
        }, this.props.hideDelayDuration ?? 0);
    }

    adjustLeftStyle(): void {
        const { align } = this.props;
        if (this.dropdown && align) {
            const { width } = this.dropdown.getBoundingClientRect();

            switch (align) {
                case ALIGN_RIGHT:
                    this.dropdown.style.right = '0';
                    // `right` alignments need no further checking / calculations, exit function
                    return;

                case ALIGN_CENTER:
                    this.dropdown.style.left = `calc(50% - ${width / 2}px)`;
                    break;

                case ALIGN_LEFT:
                    this.dropdown.style.left = '0';
                    break;
            }

            const { left } = this.dropdown.getBoundingClientRect();
            const screenWidth = document.body.getBoundingClientRect().width;

            // Check if initial position overflows window boundaries and re-position if true
            if (left + width > screenWidth) {
                const overflow = left + width - screenWidth;
                // Subtract overflow from initial position
                const halfWidth = width / 2;
                this.dropdown.style.left =
                    align === ALIGN_LEFT
                        ? `${-overflow}px`
                        : `calc(50% - ${halfWidth + overflow}px)`;
            }
        }
    }

    show(): void {
        this.setState({ isVisible: true }, () => {
            const { onShow } = this.props;
            if (onShow) {
                onShow();
            }
        });
    }

    hide(): void {
        this.setState({ isVisible: false }, () => {
            const { onHide } = this.props;
            if (onHide) {
                onHide();
            }
        });
    }

    render(): ReactNode {
        const { children, className, dropdownClassName, bodyClassName, size, hasDropdownHeader } =
            this.props;
        const { isClient, isVisible } = this.state;

        if (!children) {
            return null;
        }

        const sizeClassName = size ? styles[size] : '';

        const trigger = children[0];
        const body = children.length > 1 ? children.slice(1) : null;

        return (
            <div
                className={classnames(
                    className,
                    dibsCss.sassyFontBodySizeSmall,
                    dibsCss.textNoir,
                    dibsCss.flex,
                    dibsCss.itemsCenter,
                    dibsCss.hFull,
                    dibsCss.relative
                )}
                onMouseEnter={this.handleMouseEnter}
                onMouseLeave={this.handleMouseLeave}
            >
                {trigger}
                {isClient ? (
                    <div
                        className={classnames(
                            styles.dropdown,
                            dropdownClassName,
                            isVisible
                                ? [dibsCss.opacity100, dibsCss.visible]
                                : [dibsCss.opacity0, dibsCss.invisible],
                            hasDropdownHeader && styles.hasDropdownHeader
                        )}
                    >
                        <div
                            className={classnames(
                                styles.bodyWrapper,
                                bodyClassName,
                                sizeClassName,
                                dibsCss.zNav,
                                dibsCss.bgWhite,
                                dibsCss.absolute,
                                dibsCss.topFull
                            )}
                            ref={element => {
                                this.dropdown = element;
                            }}
                        >
                            {body}
                        </div>
                    </div>
                ) : null}
            </div>
        );
    }
}

export default Dropdown;
